import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { executeQuery } from "../../cube/api";
import { getSoldePeriodes, getArticlesByPeriode } from "../../solde/api";
import { CUBE_NAME, fetchMeasures } from "../../cube/cubeSlice";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hooks";
import { colHeaderShouldNegate, applyRule } from "../../cube/measureRules";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PeriodDashboard } from "../../cube/components/PeriodDashboard";
import ComparisonView from "../../cube/components/ComparisonView";
import EvolutionView from "../../cube/components/EvolutionView";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import SavedQueriesDropdown from "../../saved-queries/components/SavedQueriesDropdown";
import { useUserScope } from "../../auth/useUserScope";
import { dayBefore } from "../../../shared/lib/date";
import { fmtNum } from "../../../shared/lib/format";
import { card, tdS, thS } from "../../../shared/ui/styles";
import { CHIP_PALETTE, FONTS, NW } from "../../../shared/ui/tokens";
import { CUBE_DIMENSIONS } from "../../cube/dimensions";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
// Noms du cube : voir features/cube/dimensions.ts
const { dimensionName: DIM_COULEUR, hierarchyName: HIER_COULEUR, levelName: LEVEL_COULEUR } =
  CUBE_DIMENSIONS.couleur;
const { dimensionName: DIM_TAILLE, hierarchyName: HIER_TAILLE, levelName: LEVEL_TAILLE } =
  CUBE_DIMENSIONS.taille;
const TOP_OPTIONS = [5, 10, 20, 50];

// ── Nawara brand palette ──────────────────────────────────────────────────────

type ViewMode = "tableau" | "dashboard" | "comparaison" | "evolution";

const VIEW_MODES: { mode: ViewMode; icon: string; label: string }[] = [
  { mode: "tableau", icon: "📋", label: "Tableau" },
  { mode: "dashboard", icon: "📊", label: "Dashboard" },
  { mode: "comparaison", icon: "⚖️", label: "Comparaison" },
  { mode: "evolution", icon: "📈", label: "Évolution" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────
interface SoldePeriode {
  code_periode: string;
  date_debut: string | null;
  date_fin: string | null;
  date_deb_priv: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  cod_dep: string | null;
}

interface DemarquePhase {
  label: string;
  icon: string;
  dateFrom: string;
  dateTo: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function buildDemarquePhases(p: {
  date_deb_priv: string | null;
  date_debut: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  date_fin: string | null;
}): DemarquePhase[] {
  const fin = p.date_fin ? p.date_fin.slice(0, 10) : "";
  const phases: DemarquePhase[] = [];
  if (p.date_debut)
    phases.push({
      label: "Démarque 1",
      icon: "🏷️",
      dateFrom: p.date_debut.slice(0, 10),
      dateTo: p.date_debut2 ? dayBefore(p.date_debut2) : fin,
    });
  if (p.date_debut2)
    phases.push({
      label: "Démarque 2",
      icon: "🏷️",
      dateFrom: p.date_debut2.slice(0, 10),
      dateTo: p.date_debut3 ? dayBefore(p.date_debut3) : fin,
    });
  if (p.date_debut3)
    phases.push({
      label: "Démarque 3",
      icon: "🏷️",
      dateFrom: p.date_debut3.slice(0, 10),
      dateTo: p.date_debut4 ? dayBefore(p.date_debut4) : fin,
    });
  if (p.date_debut4)
    phases.push({
      label: "Démarque 4",
      icon: "🏷️",
      dateFrom: p.date_debut4.slice(0, 10),
      dateTo: fin,
    });
  return phases;
}

// ─────────────────────────────────────────────────────────────────────────────
// makeTabData — top-level pure function
// ─────────────────────────────────────────────────────────────────────────────
function makeTabData(
  columns: string[],
  allRows: any[][],
  search: string,
  selMembers: Set<string> | null,
  topN: number | null,
  cubeMeasures: any[],
) {
  const dataHeaders = columns.slice(1);
  const negateFlags = dataHeaders.map((h) =>
    colHeaderShouldNegate(h, cubeMeasures),
  );

  const puhtAchatIdx = dataHeaders.findIndex((h) => /puht/i.test(h));
  const tvaAchatIdx = dataHeaders.findIndex((h) => /\btva\b/i.test(h));
  const pttcIdx = dataHeaders.findIndex((h) => /pttc/i.test(h));
  const hasCoutTTC = puhtAchatIdx >= 0 && tvaAchatIdx >= 0;
  const hasMarge = hasCoutTTC && pttcIdx >= 0;

  const getApplied = (row: any[], idx: number): number => {
    if (idx < 0) return 0;
    const applied = applyRule(row[1 + idx], negateFlags[idx]);
    return typeof applied === "number"
      ? applied
      : parseFloat(String(applied)) || 0;
  };
  const getCoutTTC = (row: any[]) =>
    getApplied(row, puhtAchatIdx) + getApplied(row, tvaAchatIdx);
  const getMarge = (row: any[]) => getApplied(row, pttcIdx) - getCoutTTC(row);

  const filteredRows = allRows.filter((row) => {
    const rowName = String(row[0] ?? "").toLowerCase();
    if (search && !rowName.includes(search.toLowerCase())) return false;
    if (selMembers !== null && !selMembers.has(String(row[0] ?? "")))
      return false;
    return true;
  });

  const displayRows = (() => {
    const sorted = [...filteredRows].sort(
      (a, b) => getApplied(b, 0) - getApplied(a, 0),
    );
    return topN ? sorted.slice(0, topN) : sorted;
  })();

  const grandTotals = dataHeaders.map((_, i) =>
    displayRows.reduce((s, r) => s + getApplied(r, i), 0),
  );
  const totalCoutTTC = hasCoutTTC
    ? displayRows.reduce((s, r) => s + getCoutTTC(r), 0)
    : null;
  const totalMarge = hasMarge
    ? displayRows.reduce((s, r) => s + getMarge(r), 0)
    : null;

  const chartData = displayRows.map((row) => {
    const entry: { name: string; [k: string]: any } = {
      name: String(row[0] ?? ""),
    };
    dataHeaders.forEach((h, i) => {
      entry[h] = getApplied(row, i);
    });
    if (hasCoutTTC) entry["Coût TTC"] = getCoutTTC(row);
    if (hasMarge) entry["Marge"] = getMarge(row);
    return entry;
  });

  return {
    dataHeaders,
    negateFlags,
    hasCoutTTC,
    hasMarge,
    getApplied,
    getCoutTTC,
    getMarge,
    filteredRows,
    displayRows,
    grandTotals,
    totalCoutTTC,
    totalMarge,
    chartData,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// makeTabDataCombined — like makeTabData but with 2 leading dim columns
// ─────────────────────────────────────────────────────────────────────────────
function makeTabDataCombined(
  columns: string[],
  allRows: any[][],
  search: string,
  topN: number | null,
  cubeMeasures: any[],
) {
  // columns = ["Couleur", "Taille", "Measure1", ...]
  const dataHeaders = columns.slice(2);
  const negateFlags = dataHeaders.map((h) =>
    colHeaderShouldNegate(h, cubeMeasures),
  );

  const puhtAchatIdx = dataHeaders.findIndex((h) => /puht/i.test(h));
  const tvaAchatIdx = dataHeaders.findIndex((h) => /\btva\b/i.test(h));
  const pttcIdx = dataHeaders.findIndex((h) => /pttc/i.test(h));
  const hasCoutTTC = puhtAchatIdx >= 0 && tvaAchatIdx >= 0;
  const hasMarge = hasCoutTTC && pttcIdx >= 0;

  const getApplied = (row: any[], idx: number): number => {
    if (idx < 0) return 0;
    const applied = applyRule(row[2 + idx], negateFlags[idx]);
    return typeof applied === "number"
      ? applied
      : parseFloat(String(applied)) || 0;
  };
  const getCoutTTC = (row: any[]) =>
    getApplied(row, puhtAchatIdx) + getApplied(row, tvaAchatIdx);
  const getMarge = (row: any[]) => getApplied(row, pttcIdx) - getCoutTTC(row);

  const filteredRows = allRows.filter((row) => {
    const label =
      `${String(row[0] ?? "")} - ${String(row[1] ?? "")}`.toLowerCase();
    if (search && !label.includes(search.toLowerCase())) return false;
    return true;
  });

  const displayRows = (() => {
    const sorted = [...filteredRows].sort(
      (a, b) => getApplied(b, 0) - getApplied(a, 0),
    );
    return topN ? sorted.slice(0, topN) : sorted;
  })();

  const grandTotals = dataHeaders.map((_, i) =>
    displayRows.reduce((s, r) => s + getApplied(r, i), 0),
  );
  const totalCoutTTC = hasCoutTTC
    ? displayRows.reduce((s, r) => s + getCoutTTC(r), 0)
    : null;
  const totalMarge = hasMarge
    ? displayRows.reduce((s, r) => s + getMarge(r), 0)
    : null;

  return {
    dataHeaders,
    negateFlags,
    hasCoutTTC,
    hasMarge,
    getApplied,
    getCoutTTC,
    getMarge,
    filteredRows,
    displayRows,
    grandTotals,
    totalCoutTTC,
    totalMarge,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CombinedPanel — Couleur × Taille combined table
// ─────────────────────────────────────────────────────────────────────────────
interface CombinedPanelProps {
  columns: string[];
  allRows: any[][];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (s: string) => void;
  topN: number | null;
  viewMode: ViewMode;
  selMeasures: string[];
  tabData: ReturnType<typeof makeTabDataCombined>;
  // ── Filtre Couleur & Taille ──
  couleurList: string[];
  tailleList: string[];
  selCouleur: string | null;
  setSelCouleur: (v: string | null) => void;
  selTaille: string | null;
  setSelTaille: (v: string | null) => void;
}

const CombinedPanel: React.FC<CombinedPanelProps> = ({
  columns,
  allRows,
  loading,
  error,
  search,
  setSearch,
  topN,
  viewMode,
  selMeasures,
  tabData,
  couleurList,
  tailleList,
  selCouleur,
  setSelCouleur,
  selTaille,
  setSelTaille,
}) => {
  const {
    dataHeaders,
    negateFlags,
    hasCoutTTC,
    hasMarge,
    getApplied,
    getCoutTTC,
    getMarge,
    filteredRows,
    displayRows: rawDisplayRows,
    grandTotals: rawGrandTotals,
    totalCoutTTC: rawTotalCoutTTC,
    totalMarge: rawTotalMarge,
  } = tabData;

  // ── Dropdown local state ───────────────────────────────────────────────────
  const [couleurOpen, setCouleurOpen] = useState(false);
  const [couleurSearch, setCouleurSearch] = useState("");
  const [tailleOpen, setTailleOpen] = useState(false);
  const [tailleSearch, setTailleSearch] = useState("");
  const couleurDropRef = useRef<HTMLDivElement>(null);
  const tailleDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        couleurDropRef.current &&
        !couleurDropRef.current.contains(e.target as Node)
      )
        setCouleurOpen(false);
      if (
        tailleDropRef.current &&
        !tailleDropRef.current.contains(e.target as Node)
      )
        setTailleOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Apply Couleur & Taille filter on filteredRows (BEFORE topN) ───────────
  const displayRows = (() => {
    let rows = filteredRows.filter((row) => {
      if (selCouleur && String(row[0] ?? "").trim() !== selCouleur.trim())
        return false;
      if (selTaille && String(row[1] ?? "").trim() !== selTaille.trim())
        return false;
      return true;
    });
    rows = [...rows].sort((a, b) => getApplied(b, 0) - getApplied(a, 0));
    if (topN) rows = rows.slice(0, topN);
    return rows;
  })();

  const grandTotals = dataHeaders.map((_, i) =>
    displayRows.reduce((s, r) => s + getApplied(r, i), 0),
  );
  const totalCoutTTC = hasCoutTTC
    ? displayRows.reduce((s, r) => s + getCoutTTC(r), 0)
    : null;
  const totalMarge = hasMarge
    ? displayRows.reduce((s, r) => s + getMarge(r), 0)
    : null;

  const dashboardData = {
    columns:
      columns.length >= 2 ? ["Couleur × Taille", ...columns.slice(2)] : columns,
    rows: displayRows.map((row) => [
      `${String(row[0] ?? "").trim()} — ${String(row[1] ?? "").trim()}`,
      ...row.slice(2),
    ]),
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 0 }}
    >
      {/* ── Error ── */}
      {error && (
        <div
          style={{
            color: NW.danger,
            fontSize: 12,
            padding: "10px 16px",
            background: NW.dangerLight,
            border: `1px solid ${NW.danger}30`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {/* ── Colonnes calculées banner ── */}
      {(hasCoutTTC || hasMarge) && (
        <div
          style={{
            padding: "8px 14px",
            background: NW.tealLight,
            border: `1px solid ${NW.teal}33`,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            fontSize: 11,
            fontWeight: 600,
            alignItems: "center",
          }}
        >
          <span style={{ color: NW.teal }}>⚡ Colonnes calculées :</span>
          {hasCoutTTC && (
            <span
              style={{
                color: NW.teal,
                background: "rgba(0,160,140,0.12)",
                border: `1px solid ${NW.teal}44`,
                borderRadius: 6,
                padding: "2px 9px",
              }}
            >
              💎 Coût TTC
            </span>
          )}
          {hasMarge && (
            <span
              style={{
                color: NW.orange,
                background: "rgba(176,115,3,0.10)",
                border: `1px solid ${NW.orange}44`,
                borderRadius: 6,
                padding: "2px 9px",
              }}
            >
              📈 Marge
            </span>
          )}
        </div>
      )}

      {/* ── Results ── */}
      {allRows.length > 0 && (
        <div
          style={{
            ...card(),
            borderRadius: "0 0 14px 14px",
            borderTop: "none",
          }}
        >
          {/* Search + Filter bar */}
          <div
            style={{
              padding: "10px 14px",
              background: `linear-gradient(90deg, ${NW.violetSoft} 0%, ${NW.white} 100%)`,
              borderBottom: `1.5px solid ${NW.violetMed}`,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Search input */}
              <div
                style={{
                  position: "relative",
                  flex: "1 1 220px",
                  minWidth: 180,
                  maxWidth: 340,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 9,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: NW.violet,
                    fontSize: 12,
                    pointerEvents: "none",
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Rechercher une combinaison…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 28px 6px 28px",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 9,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    outline: "none",
                    color: NW.gray800,
                    background: NW.white,
                    transition: "border-color 0.15s",
                    boxSizing: "border-box",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute",
                      right: 7,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: NW.gray500,
                      fontSize: 12,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* ── Dropdown Couleur ── */}
              <div ref={couleurDropRef} style={{ position: "relative" }}>
                <button
                  onClick={() => {
                    setCouleurOpen((v) => !v);
                    setTailleOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "6px 14px",
                    border: `1.5px solid ${selCouleur ? NW.rose : NW.gray200}`,
                    borderRadius: 9,
                    background: selCouleur ? NW.roseLight : NW.white,
                    color: selCouleur ? NW.roseDark : NW.gray700,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                    minWidth: 150,
                    justifyContent: "space-between",
                    boxShadow: couleurOpen
                      ? `0 0 0 3px ${NW.roseMed}`
                      : undefined,
                    transition: "all 0.12s",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <span>🎨</span>
                    {selCouleur ?? "Couleur"}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: NW.gray500,
                      display: "inline-block",
                      transform: couleurOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▾
                  </span>
                </button>
                {selCouleur && (
                  <button
                    onClick={() => setSelCouleur(null)}
                    style={{
                      marginLeft: 4,
                      fontSize: 11,
                      padding: "3px 9px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${NW.roseMed}`,
                      background: NW.roseLight,
                      color: NW.roseDark,
                      fontWeight: 700,
                      fontFamily: FONTS.body,
                    }}
                  >
                    ✕
                  </button>
                )}

                {couleurOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 400,
                      background: NW.white,
                      border: `1.5px solid ${NW.roseMed}`,
                      borderRadius: 14,
                      boxShadow: "0 12px 40px rgba(74,134,199,0.18)",
                      width: 260,
                      maxHeight: 360,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "10px 12px 8px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: `linear-gradient(90deg, ${NW.roseSoft}, ${NW.white})`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: NW.roseDark,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginBottom: 7,
                        }}
                      >
                        🎨 Filtrer par Couleur
                      </div>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Rechercher une couleur…"
                        value={couleurSearch}
                        onChange={(e) => setCouleurSearch(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          border: `1.5px solid ${NW.roseMed}`,
                          borderRadius: 8,
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          outline: "none",
                          color: NW.gray800,
                          background: NW.white,
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    {/* Tout / Aucun */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "7px 12px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: NW.gray50,
                      }}
                    >
                      <button
                        onClick={() => {
                          setSelCouleur(null);
                          setCouleurOpen(false);
                        }}
                        style={{
                          flex: 1,
                          fontSize: 11,
                          padding: "4px 0",
                          borderRadius: 7,
                          border: `1px solid ${NW.roseMed}`,
                          background:
                            selCouleur === null ? NW.roseLight : NW.white,
                          color: selCouleur === null ? NW.roseDark : NW.gray700,
                          fontWeight: selCouleur === null ? 700 : 500,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                        }}
                      >
                        ✓ Tous
                      </button>
                    </div>
                    {/* List */}
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {couleurList
                        .filter(
                          (c) =>
                            !couleurSearch ||
                            c
                              .toLowerCase()
                              .includes(couleurSearch.toLowerCase()),
                        )
                        .map((c) => (
                          <div
                            key={c}
                            className="nw-dropdown-item"
                            onClick={() => {
                              setSelCouleur(c);
                              setCouleurOpen(false);
                              setCouleurSearch("");
                            }}
                            style={{
                              padding: "8px 14px",
                              cursor: "pointer",
                              fontSize: 12,
                              fontFamily: FONTS.body,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background:
                                selCouleur === c ? NW.roseLight : NW.white,
                              color:
                                selCouleur === c ? NW.roseDark : NW.gray800,
                              fontWeight: selCouleur === c ? 700 : 400,
                              borderBottom: `1px solid ${NW.gray100}`,
                            }}
                          >
                            {selCouleur === c && (
                              <span
                                style={{ color: NW.roseDark, fontSize: 12 }}
                              >
                                ✓
                              </span>
                            )}
                            {c}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Dropdown Taille ── */}
              <div ref={tailleDropRef} style={{ position: "relative" }}>
                <button
                  onClick={() => {
                    setTailleOpen((v) => !v);
                    setCouleurOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "6px 14px",
                    border: `1.5px solid ${selTaille ? NW.violet : NW.gray200}`,
                    borderRadius: 9,
                    background: selTaille ? NW.violetLight : NW.white,
                    color: selTaille ? NW.violet : NW.gray700,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                    minWidth: 150,
                    justifyContent: "space-between",
                    boxShadow: tailleOpen
                      ? `0 0 0 3px ${NW.violetMed}`
                      : undefined,
                    transition: "all 0.12s",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <span>📏</span>
                    {selTaille ?? "Taille"}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: NW.gray500,
                      display: "inline-block",
                      transform: tailleOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▾
                  </span>
                </button>
                {selTaille && (
                  <button
                    onClick={() => setSelTaille(null)}
                    style={{
                      marginLeft: 4,
                      fontSize: 11,
                      padding: "3px 9px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${NW.violetMed}`,
                      background: NW.violetLight,
                      color: NW.violet,
                      fontWeight: 700,
                      fontFamily: FONTS.body,
                    }}
                  >
                    ✕
                  </button>
                )}

                {tailleOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 400,
                      background: NW.white,
                      border: `1.5px solid ${NW.violetMed}`,
                      borderRadius: 14,
                      boxShadow: "0 12px 40px rgba(29,91,168,0.18)",
                      width: 260,
                      maxHeight: 360,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "10px 12px 8px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: `linear-gradient(90deg, ${NW.violetSoft}, ${NW.white})`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: NW.violet,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginBottom: 7,
                        }}
                      >
                        📏 Filtrer par Taille
                      </div>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Rechercher une taille…"
                        value={tailleSearch}
                        onChange={(e) => setTailleSearch(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          border: `1.5px solid ${NW.violetMed}`,
                          borderRadius: 8,
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          outline: "none",
                          color: NW.gray800,
                          background: NW.white,
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    {/* Tout */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "7px 12px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: NW.gray50,
                      }}
                    >
                      <button
                        onClick={() => {
                          setSelTaille(null);
                          setTailleOpen(false);
                        }}
                        style={{
                          flex: 1,
                          fontSize: 11,
                          padding: "4px 0",
                          borderRadius: 7,
                          border: `1px solid ${NW.violetMed}`,
                          background:
                            selTaille === null ? NW.violetLight : NW.white,
                          color: selTaille === null ? NW.violet : NW.gray700,
                          fontWeight: selTaille === null ? 700 : 500,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                        }}
                      >
                        ✓ Tous
                      </button>
                    </div>
                    {/* List */}
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {tailleList
                        .filter(
                          (t) =>
                            !tailleSearch ||
                            t
                              .toLowerCase()
                              .includes(tailleSearch.toLowerCase()),
                        )
                        .map((t) => (
                          <div
                            key={t}
                            className="nw-dropdown-item"
                            onClick={() => {
                              setSelTaille(t);
                              setTailleOpen(false);
                              setTailleSearch("");
                            }}
                            style={{
                              padding: "8px 14px",
                              cursor: "pointer",
                              fontSize: 12,
                              fontFamily: FONTS.body,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background:
                                selTaille === t ? NW.violetLight : NW.white,
                              color: selTaille === t ? NW.violet : NW.gray800,
                              fontWeight: selTaille === t ? 700 : 400,
                              borderBottom: `1px solid ${NW.gray100}`,
                            }}
                          >
                            {selTaille === t && (
                              <span style={{ color: NW.violet, fontSize: 12 }}>
                                ✓
                              </span>
                            )}
                            {t}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Tableau view ── */}
          {viewMode === "tableau" && (
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 520,
                animation: "nwFadeSlideIn 0.38s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <table
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  width: "100%",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr
                    style={{
                      background: NW.roseDark,
                    }}
                  >
                    <th
                      style={thS({
                        textAlign: "left",
                        color: "#FFFFFF",
                        position: "sticky",
                        left: 0,
                        background: NW.roseDark,
                        zIndex: 11,
                        minWidth: 80,
                        fontFamily: FONTS.heading,
                        borderRight: `2px solid rgba(255,255,255,0.2)`,
                      })}
                    >
                      🎨 Couleur
                    </th>
                    <th
                      style={thS({
                        textAlign: "left",
                        color: "#FFFFFF",
                        position: "sticky",
                        left: 80,
                        background: NW.violetDark,
                        zIndex: 11,
                        minWidth: 80,
                        fontFamily: FONTS.heading,
                        borderRight: `2px solid rgba(255,255,255,0.2)`,
                      })}
                    >
                      📏 Taille
                    </th>
                    {dataHeaders.map((h) => (
                      <th
                        key={h}
                        style={thS({
                          color: NW.roseMed,
                          fontFamily: FONTS.heading,
                          minWidth: 100,
                        })}
                      >
                        {h}
                      </th>
                    ))}
                    {hasCoutTTC && (
                      <th
                        style={thS({
                          color: "#A9DFD7",
                          background: "rgba(0,160,140,0.18)",
                          borderLeft: "3px solid rgba(0,160,140,0.4)",
                          fontFamily: FONTS.heading,
                        })}
                      >
                        💎 Coût TTC
                      </th>
                    )}
                    {hasMarge && (
                      <th
                        style={thS({
                          color: "#FCE7BF",
                          background: "rgba(176,115,3,0.18)",
                          borderLeft: "3px solid rgba(176,115,3,0.4)",
                          fontFamily: FONTS.heading,
                        })}
                      >
                        📈 Marge
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, ri) => {
                    const coutTTC = hasCoutTTC ? getCoutTTC(row) : null;
                    const marge = hasMarge ? getMarge(row) : null;
                    const margePos = marge !== null && marge >= 0;
                    const chipColor = CHIP_PALETTE[ri % CHIP_PALETTE.length];
                    const couleur = String(row[0] ?? "");
                    const taille = String(row[1] ?? "");
                    return (
                      <tr
                        key={ri}
                        className="nw-row-hover"
                        style={{
                          background: ri % 2 === 0 ? NW.white : NW.gray50,
                          transition: "background 0.12s",
                        }}
                      >
                        {/* Couleur cell */}
                        <td
                          style={tdS({
                            fontWeight: 700,
                            color: NW.roseDark,
                            position: "sticky",
                            left: 0,
                            background: ri % 2 === 0 ? NW.white : NW.gray50,
                            zIndex: 5,
                            borderRight: `2px solid ${NW.gray200}`,
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          })}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                            }}
                          >
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: chipColor,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {couleur}
                            </span>
                          </div>
                        </td>
                        {/* Taille cell */}
                        <td
                          style={tdS({
                            fontWeight: 700,
                            color: NW.violet,
                            position: "sticky",
                            left: 80,
                            background: ri % 2 === 0 ? NW.white : NW.gray50,
                            zIndex: 5,
                            borderRight: `2px solid ${NW.gray200}`,
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          })}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                background: `${chipColor}18`,
                                color: chipColor,
                                border: `1px solid ${chipColor}33`,
                                borderRadius: 5,
                                padding: "1px 5px",
                                letterSpacing: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              #{ri + 1}
                            </span>
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {taille}
                            </span>
                          </div>
                        </td>
                        {dataHeaders.map((h, i) => {
                          const val = applyRule(row[2 + i], negateFlags[i]);
                          const num =
                            typeof val === "number"
                              ? val
                              : parseFloat(String(val)) || null;
                          const isNeg = num !== null && num < 0;
                          return (
                            <td
                              key={h}
                              style={tdS({
                                textAlign: "right",
                                color: isNeg ? NW.danger : NW.gray800,
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {num !== null ? fmtNum(num) : "—"}
                            </td>
                          );
                        })}
                        {hasCoutTTC && coutTTC !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              color: NW.teal,
                              fontWeight: 600,
                              background: "rgba(0,160,140,0.04)",
                              borderLeft: `3px solid ${NW.teal}22`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            {fmtNum(coutTTC)}
                          </td>
                        )}
                        {hasMarge && marge !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              fontWeight: 700,
                              color: margePos ? NW.success : NW.danger,
                              background: margePos
                                ? "rgba(0,160,140,0.07)"
                                : "rgba(214,69,69,0.07)",
                              borderLeft: `3px solid ${margePos ? NW.success : NW.danger}44`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 9,
                                  animation: margePos
                                    ? "nwPulseGreen 2s infinite"
                                    : "nwPulseRed 2s infinite",
                                  borderRadius: "50%",
                                  width: 7,
                                  height: 7,
                                  background: margePos ? NW.success : NW.danger,
                                  display: "inline-block",
                                  flexShrink: 0,
                                }}
                              />
                              {margePos ? "" : "−"}
                              {fmtNum(Math.abs(marge))}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {displayRows.length > 1 && (
                  <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                    <tr>
                      <td
                        colSpan={2}
                        style={tdS({
                          background: NW.violetDark,
                          color: "#FFFFFF",
                          fontWeight: 800,
                          fontSize: 12,
                          fontFamily: FONTS.heading,
                          borderRight: `3px solid ${NW.rose}`,
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                        })}
                      >
                        ∑ TOTAL GÉNÉRAL
                      </td>
                      {grandTotals.map((val, i) => (
                        <td
                          key={i}
                          style={tdS({
                            background: NW.violetDark,
                            color: NW.roseMed,
                            fontWeight: 700,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(val)}
                        </td>
                      ))}
                      {hasCoutTTC && totalCoutTTC !== null && (
                        <td
                          style={tdS({
                            background: NW.teal,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: "3px solid rgba(255,255,255,0.2)",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(totalCoutTTC)}
                        </td>
                      )}
                      {hasMarge && totalMarge !== null && (
                        <td
                          style={tdS({
                            background:
                              totalMarge >= 0 ? NW.success : NW.danger,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: "3px solid rgba(255,255,255,0.2)",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {totalMarge >= 0 ? "+" : "−"}
                          {fmtNum(Math.abs(totalMarge))}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ── Dashboard view ── */}
          {viewMode === "dashboard" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <PeriodDashboard
                data={dashboardData}
                rowDimCount={1}
                topN={topN}
              />
            </div>
          )}

          {/* ── Comparaison view ── */}
          {viewMode === "comparaison" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.roseDark,
                    marginBottom: 8,
                    fontFamily: FONTS.heading,
                  }}
                >
                  🎨 Par Couleur
                </div>
                <ComparisonView
                  defaultMeasures={selMeasures}
                  defaultDimension={DIM_COULEUR}
                  defaultHierarchy={HIER_COULEUR}
                  defaultLevel={LEVEL_COULEUR}
                  overrideTopN={topN}
                  saveType="couleur-taille"
                />
              </div>
              <div
                style={{
                  borderTop: `1.5px dashed ${NW.gray200}`,
                  paddingTop: 16,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.violet,
                    marginBottom: 8,
                    fontFamily: FONTS.heading,
                  }}
                >
                  📏 Par Taille
                </div>
                <ComparisonView
                  defaultMeasures={selMeasures}
                  defaultDimension={DIM_TAILLE}
                  defaultHierarchy={HIER_TAILLE}
                  defaultLevel={LEVEL_TAILLE}
                  overrideTopN={topN}
                  saveType="couleur-taille"
                />
              </div>
            </div>
          )}

          {/* ── Évolution view ── */}
          {viewMode === "evolution" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.roseDark,
                    marginBottom: 8,
                    fontFamily: FONTS.heading,
                  }}
                >
                  🎨 Par Couleur
                </div>
                <EvolutionView
                  defaultMeasure={selMeasures[0] ?? ""}
                  defaultDimension={DIM_COULEUR}
                  defaultHierarchy={HIER_COULEUR}
                  defaultLevel={LEVEL_COULEUR}
                />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.violet,
                    marginBottom: 8,
                    fontFamily: FONTS.heading,
                  }}
                >
                  📏 Par Taille
                </div>
                <EvolutionView
                  defaultMeasure={selMeasures[0] ?? ""}
                  defaultDimension={DIM_TAILLE}
                  defaultHierarchy={HIER_TAILLE}
                  defaultLevel={LEVEL_TAILLE}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Empty state ── */}
      {allRows.length === 0 && !loading && !error && (
        <div
          style={{
            ...card({ borderRadius: "0 0 14px 14px", borderTop: "none" }),
            color: NW.gray500,
            padding: 40,
            textAlign: "center",
            fontSize: 14,
            border: `1.5px dashed ${NW.violetMed}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 40 }}>🎨📏</span>
          <div
            style={{
              fontWeight: 800,
              color: NW.violet,
              fontSize: 15,
              fontFamily: FONTS.heading,
            }}
          >
            Aucune donnée combinée
          </div>
          <div style={{ fontSize: 12, color: NW.gray500, lineHeight: 1.6 }}>
            Sélectionnez des mesures puis cliquez{" "}
            <strong style={{ color: NW.violet }}>▶ Calculer</strong>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AnalysisPanel — renders one analysis (couleur OR taille)
// ─────────────────────────────────────────────────────────────────────────────
interface AnalysisPanelProps {
  isC: boolean;
  dimName: string;
  hierName: string;
  columns: string[];
  allRows: any[][];
  loading: boolean;
  error: string | null;
  members: string[];
  loadingMembers: boolean;
  search: string;
  setSearch: (s: string) => void;
  selMembers: Set<string> | null;
  setSelMembers: (s: Set<string> | null) => void;
  filterOpen: boolean;
  setFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filterSearch: string;
  setFilterSearch: (s: string) => void;
  topN: number | null;
  viewMode: ViewMode;
  selMeasures: string[];
  tabData: ReturnType<typeof makeTabData>;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({
  isC,
  dimName,
  hierName,
  columns,
  allRows,
  loading,
  error,
  members,
  loadingMembers,
  search,
  setSearch,
  selMembers,
  setSelMembers,
  filterOpen,
  setFilterOpen,
  filterSearch,
  setFilterSearch,
  topN,
  viewMode,
  selMeasures,
  tabData,
}) => {
  const {
    dataHeaders,
    negateFlags,
    hasCoutTTC,
    hasMarge,
    getApplied,
    getCoutTTC,
    getMarge,
    displayRows,
    grandTotals,
    totalCoutTTC,
    totalMarge,
    chartData,
  } = tabData;

  const memberList =
    members.length > 0
      ? members
      : allRows.map((r) => String(r[0] ?? "")).filter(Boolean);

  const toggleMember = (m: string) => {
    const base =
      selMembers === null ? new Set(memberList) : new Set(selMembers);
    if (base.has(m)) base.delete(m);
    else base.add(m);
    setSelMembers(new Set(base));
  };

  const dashboardData = { columns, rows: allRows };
  const label = isC ? "Couleur" : "Taille";
  const icon = isC ? "🎨" : "📏";
  const accent = isC ? NW.rose : NW.violet;
  const accentLight = isC ? NW.roseLight : NW.violetLight;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 0 }}
    >
      {/* ── Panel header ── */}
      <div
        style={{
          background: isC
            ? `linear-gradient(135deg, ${NW.roseDark} 0%, ${NW.rose} 100%)`
            : `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 100%)`,
          borderRadius: "14px 14px 0 0",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 20 }}>{icon}</span>
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 15,
              color: "#FFFFFF",
            }}
          >
            Analyse par {label}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.55)",
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginTop: 1,
            }}
          >
            {allRows.length} {isC ? "couleurs" : "tailles"} chargées
          </div>
        </div>
        {allRows.length > 0 && (
          <div
            style={{
              marginLeft: "auto",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 10,
              padding: "6px 16px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#FFFFFF",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {allRows.length}
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,0.6)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {isC ? "Couleurs" : "Tailles"}
            </div>
          </div>
        )}
        {loading && (
          <span
            style={{
              marginLeft: "auto",
              color: "rgba(255,255,255,0.7)",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span className="spinner" style={{ width: 12, height: 12 }} />
            Chargement…
          </span>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            color: NW.danger,
            fontSize: 12,
            padding: "10px 16px",
            background: NW.dangerLight,
            border: `1px solid ${NW.danger}30`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {/* ── Colonnes calculées banner ── */}
      {(hasCoutTTC || hasMarge) && (
        <div
          style={{
            padding: "8px 14px",
            background: NW.tealLight,
            border: `1px solid ${NW.teal}33`,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            fontSize: 11,
            fontWeight: 600,
            alignItems: "center",
          }}
        >
          <span style={{ color: NW.teal }}>⚡ Colonnes calculées :</span>
          {hasCoutTTC && (
            <span
              style={{
                color: NW.teal,
                background: "rgba(0,160,140,0.12)",
                border: `1px solid ${NW.teal}44`,
                borderRadius: 6,
                padding: "2px 9px",
              }}
            >
              💎 Coût TTC
            </span>
          )}
          {hasMarge && (
            <span
              style={{
                color: NW.orange,
                background: "rgba(176,115,3,0.10)",
                border: `1px solid ${NW.orange}44`,
                borderRadius: 6,
                padding: "2px 9px",
              }}
            >
              📈 Marge
            </span>
          )}
        </div>
      )}

      {/* ── Results ── */}
      {allRows.length > 0 && (
        <div
          style={{
            ...card(),
            borderRadius: "0 0 14px 14px",
            borderTop: "none",
          }}
        >
          {/* Filter bar */}
          <div
            style={{
              padding: "10px 14px",
              background: `linear-gradient(90deg, ${NW.violetSoft} 0%, ${NW.white} 100%)`,
              borderBottom: `1.5px solid ${NW.violetMed}`,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Multi-select dropdown */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setFilterOpen((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "6px 14px",
                    border: `1.5px solid ${selMembers !== null ? NW.violet : NW.gray200}`,
                    borderRadius: 9,
                    background: selMembers !== null ? NW.violetSoft : NW.white,
                    color: selMembers !== null ? NW.violet : NW.gray700,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                    minWidth: 170,
                    justifyContent: "space-between",
                    boxShadow: filterOpen
                      ? `0 0 0 3px ${NW.violetMed}`
                      : undefined,
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <span style={{ fontSize: 13 }}>{icon}</span>
                    {selMembers === null
                      ? `${memberList.length} / ${memberList.length} ${isC ? "couleurs" : "tailles"}`
                      : `${selMembers.size} / ${memberList.length} sél.`}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: NW.gray500,
                      display: "inline-block",
                      transform: filterOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▾
                  </span>
                </button>

                {filterOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 300,
                      background: NW.white,
                      border: `1.5px solid ${NW.violetMed}`,
                      borderRadius: 14,
                      boxShadow: "0 12px 40px rgba(29,91,168,0.18)",
                      width: 280,
                      height: 380,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px 8px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: `linear-gradient(90deg, ${NW.violetSoft}, ${NW.white})`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: NW.violet,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginBottom: 7,
                        }}
                      >
                        🔍 Filtrer les {isC ? "couleurs" : "tailles"}
                      </div>
                      <input
                        autoFocus
                        type="text"
                        placeholder={`Rechercher une ${isC ? "couleur" : "taille"}…`}
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          border: `1.5px solid ${NW.violetMed}`,
                          borderRadius: 8,
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          outline: "none",
                          color: NW.gray800,
                          background: NW.white,
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "7px 12px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: NW.gray50,
                      }}
                    >
                      <button
                        onClick={() => setSelMembers(null)}
                        style={{
                          flex: 1,
                          fontSize: 11,
                          padding: "4px 0",
                          borderRadius: 7,
                          border: `1px solid ${NW.violetMed}`,
                          background:
                            selMembers === null ? NW.violetLight : NW.white,
                          color: selMembers === null ? NW.violet : NW.gray700,
                          fontWeight: selMembers === null ? 700 : 500,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                        }}
                      >
                        ✓ Tout
                      </button>
                      <button
                        onClick={() => setSelMembers(new Set())}
                        style={{
                          flex: 1,
                          fontSize: 11,
                          padding: "4px 0",
                          borderRadius: 7,
                          border: `1px solid ${NW.gray200}`,
                          background: NW.white,
                          color: NW.gray500,
                          fontWeight: 500,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                        }}
                      >
                        ✕ Aucun
                      </button>
                    </div>
                    <div
                      style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}
                    >
                      {loadingMembers && memberList.length === 0 && (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            fontSize: 12,
                            color: NW.gray500,
                            fontStyle: "italic",
                          }}
                        >
                          Chargement…
                        </div>
                      )}
                      {memberList
                        .filter((n) =>
                          filterSearch
                            ? n
                                .toLowerCase()
                                .includes(filterSearch.toLowerCase())
                            : true,
                        )
                        .map((name, i) => {
                          const checked =
                            selMembers === null || selMembers.has(name);
                          const chipColor =
                            CHIP_PALETTE[i % CHIP_PALETTE.length];
                          return (
                            <div
                              key={name}
                              className="nw-dropdown-item"
                              onClick={() => toggleMember(name)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 9,
                                padding: "6px 12px",
                                cursor: "pointer",
                                background: checked
                                  ? `${chipColor}0C`
                                  : "transparent",
                                transition: "background 0.1s",
                              }}
                            >
                              <div
                                style={{
                                  width: 15,
                                  height: 15,
                                  borderRadius: 4,
                                  border: `2px solid ${checked ? chipColor : NW.gray300}`,
                                  background: checked
                                    ? chipColor
                                    : "transparent",
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 9,
                                  color: "#FFFFFF",
                                  transition: "all 0.12s",
                                }}
                              >
                                {checked && "✓"}
                              </div>
                              <span
                                style={{
                                  fontSize: 12,
                                  color: checked ? NW.gray800 : NW.gray500,
                                  fontWeight: checked ? 600 : 400,
                                  fontFamily: FONTS.body,
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {name}
                              </span>
                              <span
                                style={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  background: chipColor,
                                  flexShrink: 0,
                                  opacity: checked ? 1 : 0.3,
                                }}
                              />
                            </div>
                          );
                        })}
                    </div>
                    <div
                      style={{
                        padding: "8px 12px",
                        borderTop: `1px solid ${NW.gray100}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: NW.gray50,
                      }}
                    >
                      <span style={{ fontSize: 11, color: NW.gray500 }}>
                        {selMembers === null
                          ? memberList.length
                          : selMembers.size}{" "}
                        sélectionné(s)
                      </span>
                      <button
                        onClick={() => setFilterOpen(false)}
                        style={{
                          fontSize: 11,
                          padding: "4px 14px",
                          borderRadius: 7,
                          border: "none",
                          background: NW.violet,
                          color: "#FFFFFF",
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                        }}
                      >
                        Appliquer ✓
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Search bar */}
              <div style={{ position: "relative", flex: 1, minWidth: 120 }}>
                <span
                  style={{
                    position: "absolute",
                    left: 9,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: NW.violet,
                    fontSize: 12,
                    pointerEvents: "none",
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder={`Rechercher une ${isC ? "couleur" : "taille"}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 28px 6px 28px",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 9,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    outline: "none",
                    color: NW.gray800,
                    background: NW.white,
                    transition: "border-color 0.15s",
                    boxSizing: "border-box",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute",
                      right: 7,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: NW.gray500,
                      fontSize: 12,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Active chips */}
            {selMembers !== null && selMembers.size > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 5,
                  marginTop: 8,
                }}
              >
                {Array.from(selMembers).map((m, i) => {
                  const chipColor =
                    CHIP_PALETTE[memberList.indexOf(m) % CHIP_PALETTE.length] ||
                    NW.violet;
                  return (
                    <span
                      key={m}
                      className="nw-chip-btn"
                      onClick={() => toggleMember(m)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 9px",
                        borderRadius: 99,
                        background: `${chipColor}15`,
                        color: chipColor,
                        border: `1.5px solid ${chipColor}33`,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          background: chipColor,
                          flexShrink: 0,
                        }}
                      />
                      {m}
                      <span style={{ fontSize: 9, opacity: 0.7 }}>✕</span>
                    </span>
                  );
                })}
                <button
                  onClick={() => setSelMembers(null)}
                  style={{
                    fontSize: 11,
                    padding: "2px 9px",
                    borderRadius: 99,
                    border: `1.5px solid ${NW.danger}33`,
                    background: NW.dangerLight,
                    color: NW.danger,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontFamily: FONTS.body,
                  }}
                >
                  ✕ Effacer
                </button>
              </div>
            )}
          </div>

          {/* ── Tableau view ── */}
          {viewMode === "tableau" && (
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 520,
                animation: "nwFadeSlideIn 0.38s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <table
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  width: "100%",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr
                    style={{
                      background: isC
                        ? `linear-gradient(90deg, ${NW.roseDark} 0%, ${NW.rose} 100%)`
                        : `linear-gradient(90deg, ${NW.violetDark} 0%, ${NW.violet} 100%)`,
                    }}
                  >
                    <th
                      style={thS({
                        textAlign: "left",
                        color: "#FFFFFF",
                        position: "sticky",
                        left: 0,
                        background: isC ? NW.roseDark : NW.violetDark,
                        zIndex: 11,
                        minWidth: 140,
                        fontFamily: FONTS.heading,
                        borderRight: `2px solid rgba(255,255,255,0.2)`,
                      })}
                    >
                      {icon} {label}
                    </th>
                    {dataHeaders.map((h) => (
                      <th
                        key={h}
                        style={thS({
                          color: isC ? NW.roseMed : NW.roseMed,
                          fontFamily: FONTS.heading,
                          minWidth: 100,
                        })}
                      >
                        {h}
                      </th>
                    ))}
                    {hasCoutTTC && (
                      <th
                        style={thS({
                          color: "#A9DFD7",
                          background: "rgba(0,160,140,0.18)",
                          borderLeft: "3px solid rgba(0,160,140,0.4)",
                          fontFamily: FONTS.heading,
                        })}
                      >
                        💎 Coût TTC
                      </th>
                    )}
                    {hasMarge && (
                      <th
                        style={thS({
                          color: "#FCE7BF",
                          background: "rgba(176,115,3,0.18)",
                          borderLeft: "3px solid rgba(176,115,3,0.4)",
                          fontFamily: FONTS.heading,
                        })}
                      >
                        📈 Marge
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, ri) => {
                    const coutTTC = hasCoutTTC ? getCoutTTC(row) : null;
                    const marge = hasMarge ? getMarge(row) : null;
                    const margePos = marge !== null && marge >= 0;
                    const chipColor = CHIP_PALETTE[ri % CHIP_PALETTE.length];
                    return (
                      <tr
                        key={ri}
                        className="nw-row-hover"
                        style={{
                          background: ri % 2 === 0 ? NW.white : NW.gray50,
                          transition: "background 0.12s",
                        }}
                      >
                        <td
                          style={tdS({
                            fontWeight: 700,
                            color: NW.gray800,
                            position: "sticky",
                            left: 0,
                            background: ri % 2 === 0 ? NW.white : NW.gray50,
                            zIndex: 5,
                            borderRight: `2px solid ${NW.gray200}`,
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          })}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                            }}
                          >
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: chipColor,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {String(row[0] ?? "")}
                            </span>
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                background: `${chipColor}18`,
                                color: chipColor,
                                border: `1px solid ${chipColor}33`,
                                borderRadius: 5,
                                padding: "1px 5px",
                                letterSpacing: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              #{ri + 1}
                            </span>
                          </div>
                        </td>
                        {dataHeaders.map((h, i) => {
                          const val = applyRule(row[1 + i], negateFlags[i]);
                          const num =
                            typeof val === "number"
                              ? val
                              : parseFloat(String(val)) || null;
                          const isNeg = num !== null && num < 0;
                          return (
                            <td
                              key={h}
                              style={tdS({
                                textAlign: "right",
                                color: isNeg ? NW.danger : NW.gray800,
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {num !== null ? fmtNum(num) : "—"}
                            </td>
                          );
                        })}
                        {hasCoutTTC && coutTTC !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              color: NW.teal,
                              fontWeight: 600,
                              background: "rgba(0,160,140,0.04)",
                              borderLeft: `3px solid ${NW.teal}22`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            {fmtNum(coutTTC)}
                          </td>
                        )}
                        {hasMarge && marge !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              fontWeight: 700,
                              color: margePos ? NW.success : NW.danger,
                              background: margePos
                                ? "rgba(0,160,140,0.07)"
                                : "rgba(214,69,69,0.07)",
                              borderLeft: `3px solid ${margePos ? NW.success : NW.danger}44`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 9,
                                  animation: margePos
                                    ? "nwPulseGreen 2s infinite"
                                    : "nwPulseRed 2s infinite",
                                  borderRadius: "50%",
                                  width: 7,
                                  height: 7,
                                  background: margePos ? NW.success : NW.danger,
                                  display: "inline-block",
                                  flexShrink: 0,
                                }}
                              />
                              {margePos ? "" : "−"}
                              {fmtNum(Math.abs(marge))}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {displayRows.length > 1 && (
                  <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                    <tr>
                      <td
                        style={tdS({
                          background: isC ? NW.roseDark : NW.violetDark,
                          color: "#FFFFFF",
                          fontWeight: 800,
                          fontSize: 12,
                          fontFamily: FONTS.heading,
                          borderRight: `3px solid ${isC ? NW.rose : NW.rose}`,
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                        })}
                      >
                        ∑ TOTAL GÉNÉRAL
                      </td>
                      {grandTotals.map((val, i) => (
                        <td
                          key={i}
                          style={tdS({
                            background: isC ? NW.roseDark : NW.violetDark,
                            color: NW.roseMed,
                            fontWeight: 700,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(val)}
                        </td>
                      ))}
                      {hasCoutTTC && totalCoutTTC !== null && (
                        <td
                          style={tdS({
                            background: NW.teal,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: "3px solid rgba(255,255,255,0.2)",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(totalCoutTTC)}
                        </td>
                      )}
                      {hasMarge && totalMarge !== null && (
                        <td
                          style={tdS({
                            background:
                              totalMarge >= 0 ? NW.success : NW.danger,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: "3px solid rgba(255,255,255,0.2)",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {totalMarge >= 0 ? "+" : "−"}
                          {fmtNum(Math.abs(totalMarge))}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ── Dashboard view ── */}
          {viewMode === "dashboard" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <PeriodDashboard data={dashboardData} rowDimCount={1} />
            </div>
          )}

          {/* ── Comparaison view ── */}
          {viewMode === "comparaison" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <ComparisonView
                defaultMeasures={selMeasures}
                defaultDimension={dimName}
                defaultHierarchy={hierName}
                defaultLevel={hierName}
                overrideTopN={topN}
                saveType="couleur-taille"
              />
            </div>
          )}

          {/* ── Évolution view ── */}
          {viewMode === "evolution" && (
            <div
              style={{
                padding: 16,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <EvolutionView
                defaultMeasure={selMeasures[0] ?? ""}
                defaultDimension={dimName}
                defaultHierarchy={hierName}
                defaultLevel={hierName}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Empty state ── */}
      {allRows.length === 0 && !loading && !error && (
        <div
          style={{
            ...card({ borderRadius: "0 0 14px 14px", borderTop: "none" }),
            color: NW.gray500,
            padding: 40,
            textAlign: "center",
            fontSize: 14,
            border: `1.5px dashed ${NW.violetMed}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 40 }}>{icon}</span>
          <div
            style={{
              fontWeight: 800,
              color: NW.violet,
              fontSize: 15,
              fontFamily: FONTS.heading,
            }}
          >
            Aucune donnée
          </div>
          <div style={{ fontSize: 12, color: NW.gray500, lineHeight: 1.6 }}>
            Sélectionnez des mesures puis cliquez{" "}
            <strong style={{ color: NW.violet }}>▶ Calculer</strong>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CouleurTailleView
// ─────────────────────────────────────────────────────────────────────────────
const CouleurTailleView: React.FC = () => {
  const dispatch = useAppDispatch();
  const cubeMeasures = useAppSelector((s) => s.cube.measures);

  useEffect(() => {
    if (cubeMeasures.length === 0) dispatch(fetchMeasures());
  }, [dispatch, cubeMeasures.length]);

  // ── Measures groups ───────────────────────────────────────────────────────
  const venteMeasures = cubeMeasures.filter((m) =>
    m.measureGroup.toLowerCase().includes("vente"),
  );
  const achatMeasures = cubeMeasures.filter((m) =>
    m.measureGroup.toLowerCase().includes("achat"),
  );
  const otherMeasures = cubeMeasures.filter(
    (m) =>
      !m.measureGroup.toLowerCase().includes("vente") &&
      !m.measureGroup.toLowerCase().includes("achat"),
  );

  // ── Shared state ──────────────────────────────────────────────────────────
  const [selMeasures, setSelMeasures] = useState<string[]>([]);
  const [showMeasurePickerVente, setShowMeasurePickerVente] = useState(false);
  const [showMeasurePickerAchat, setShowMeasurePickerAchat] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [popupInitialMeasures, setPopupInitialMeasures] = useState<string[]>(
    [],
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("tableau");
  const [topN, setTopN] = useState<number | null>(10);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRefreshToken, setSavedRefreshToken] = useState(0);
  const [hasRun, setHasRun] = useState(false);

  // ── Per-tab state (combined couleur × taille) ─────────────────────────────
  const [columnsCombined, setColumnsCombined] = useState<string[]>([]);
  const [allRowsCombined, setAllRowsCombined] = useState<any[][]>([]);
  const [loadingCombined, setLoadingCombined] = useState(false);
  const [errorCombined, setErrorCombined] = useState<string | null>(null);
  const [searchCombined, setSearchCombined] = useState("");
  // ── Filtres Couleur & Taille ───────────────────────────────────────────────
  const [selCouleur, setSelCouleur] = useState<string | null>(null);
  const [selTaille, setSelTaille] = useState<string | null>(null);

  // ── Solde / Périodes (shared) ─────────────────────────────────────────────
  const [soldeOpen, setSoldeOpen] = useState(false);
  const [soldePeriodes, setSoldePeriodes] = useState<SoldePeriode[]>([]);
  const [soldeLoading, setSoldeLoading] = useState(false);
  const [soldeError, setSoldeError] = useState<string | null>(null);
  const [soldeSelected, setSoldeSelected] = useState<SoldePeriode | null>(null);
  const [soldeArticleCodes, setSoldeArticleCodes] = useState<Set<string>>(
    new Set(),
  );
  const soldeDropRef = useRef<HTMLDivElement>(null);

  // ── Démarque (shared) ─────────────────────────────────────────────────────
  const [demarqueOpen, setDemarqueOpen] = useState(false);
  const [selectedDemarquePhase, setSelectedDemarquePhase] =
    useState<DemarquePhase | null>(null);
  const demarqueDropRef = useRef<HTMLDivElement>(null);

  // ── Magasin (filtre par dépôt) — multi-sélection checkboxes ───────────────
  const [magasinOpen, setMagasinOpen] = useState(false);
  const [magasinList, setMagasinList] = useState<string[]>([]);
  const [magasinLoading, setMagasinLoading] = useState(false);
  const [selectedMagasins, setSelectedMagasins] = useState<Set<string>>(
    new Set(),
  );
  const [magasinSearch, setMagasinSearch] = useState("");
  const magasinDropRef = useRef<HTMLDivElement>(null);

  // ── Rôle utilisateur ──────────────────────────────────────────────────────
  const { userDepot, isRestricted } = useUserScope();

  // Si rôle restreint → forcer le dépôt dès que userDepot est connu
  useEffect(() => {
    if (isRestricted && userDepot) {
      setSelectedMagasins(new Set([userDepot]));
    }
  }, [isRestricted, userDepot]); // eslint-disable-line

  // ── Close dropdowns on outside click ──────────────────────────────────────
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        soldeDropRef.current &&
        !soldeDropRef.current.contains(e.target as Node)
      )
        setSoldeOpen(false);
      if (
        demarqueDropRef.current &&
        !demarqueDropRef.current.contains(e.target as Node)
      )
        setDemarqueOpen(false);
      if (
        magasinDropRef.current &&
        !magasinDropRef.current.contains(e.target as Node)
      )
        setMagasinOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Charger la liste des magasins (admin uniquement) ──────────────────────
  const loadMagasins = useCallback(async () => {
    setMagasinLoading(true);
    setMagasinList([]);
    try {
      const res = await executeQuery({
        cubeName: CUBE_NAME,
        measures: ["PTTC"],
        rows: [
          {
            ...CUBE_DIMENSIONS.depot,
          },
        ],
        columns: [],
      });
      const rows: any[][] = res.data?.rows ?? [];
      const depots: string[] = rows
        .filter((row) => {
          const name = String(row[0] ?? "")
            .trim()
            .toLowerCase();
          if (!name || name === "all") return false;
          const val = row[1];
          return typeof val === "number" && Math.abs(val) > 0;
        })
        .map((row) => String(row[0] ?? "").trim());
      setMagasinList(depots);
    } catch {
      setMagasinList([]);
    } finally {
      setMagasinLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isRestricted) loadMagasins();
  }, [isRestricted]); // eslint-disable-line

  const handleMagasinOpen = () => setMagasinOpen((v) => !v);
  const handleMagasinToggle = (mag: string) => {
    setSelectedMagasins((prev) => {
      const next = new Set(prev);
      if (next.has(mag)) next.delete(mag);
      else next.add(mag);
      return next;
    });
  };
  const handleMagasinClear = () => {
    setSelectedMagasins(new Set());
    setMagasinSearch("");
    setMagasinOpen(false);
  };
  const handleMagasinApply = () => {
    setMagasinOpen(false);
    setMagasinSearch("");
  };

  // ── Auto-select PTTC ─────────────────────────────────────────────────────
  useEffect(() => {
    if (venteMeasures.length > 0 && selMeasures.length === 0) {
      const pttc = venteMeasures.find((m) => /pttc/i.test(m.name));
      setSelMeasures([pttc ? pttc.name : venteMeasures[0].name]);
    }
  }, [venteMeasures.length]); // eslint-disable-line

  const toggleMeasure = (mName: string) =>
    setSelMeasures((prev) =>
      prev.includes(mName) ? prev.filter((x) => x !== mName) : [...prev, mName],
    );

  // ── Solde handlers ────────────────────────────────────────────────────────
  const handleSoldeOpen = async () => {
    setSoldeOpen((v) => !v);
    if (soldePeriodes.length > 0) return;
    setSoldeLoading(true);
    setSoldeError(null);
    try {
      const { data } = await getSoldePeriodes();
      setSoldePeriodes(
        data.filter((p: SoldePeriode) => p.date_debut && p.date_fin),
      );
    } catch (err: any) {
      setSoldeError(err.message ?? "Erreur chargement périodes");
    } finally {
      setSoldeLoading(false);
    }
  };

  const handleSoldePeriodeSelect = async (p: SoldePeriode) => {
    setSoldeSelected(p);
    setSoldeOpen(false);
    setSelectedDemarquePhase(null);
    setDemarqueOpen(false);
    const from = p.date_debut ? p.date_debut.slice(0, 10) : "";
    const to = p.date_fin ? p.date_fin.slice(0, 10) : "";
    setDateFrom(from);
    setDateTo(to);
    try {
      const { data: articles } = await getArticlesByPeriode(p.code_periode);
      setSoldeArticleCodes(
        new Set<string>(
          articles
            .map((a: any) => (a.cod_art ?? "").toString())
            .filter(Boolean),
        ),
      );
    } catch {
      setSoldeArticleCodes(new Set());
    }
  };

  const handleSoldeClear = () => {
    setSoldeSelected(null);
    setSoldeArticleCodes(new Set());
    setDateFrom("");
    setDateTo("");
    setSelectedDemarquePhase(null);
    setDemarqueOpen(false);
  };

  const handleDemarquePhaseSelect = (phase: DemarquePhase) => {
    setSelectedDemarquePhase(phase);
    setDateFrom(phase.dateFrom);
    setDateTo(phase.dateTo);
    setDemarqueOpen(false);
  };

  const handleDemarqueClear = () => {
    setSelectedDemarquePhase(null);
    if (soldeSelected) {
      setDateFrom(soldeSelected.date_debut?.slice(0, 10) ?? "");
      setDateTo(soldeSelected.date_fin?.slice(0, 10) ?? "");
    }
  };

  const setQuickPeriod = (type: "week" | "month" | "year") => {
    const d = new Date();
    const from = new Date(d);
    if (type === "week") from.setDate(d.getDate() - 7);
    else if (type === "month") from.setMonth(d.getMonth() - 1);
    else from.setFullYear(d.getFullYear() - 1);
    setDateFrom(from.toISOString().slice(0, 10));
    setDateTo(d.toISOString().slice(0, 10));
  };

  // ── Run query (Couleur × Taille combined) ────────────────────────────────
  const runCombined = useCallback(
    async (
      measures: string[],
      from: string,
      to: string,
      magasinFilter?: Set<string> | null,
    ) => {
      if (measures.length === 0) return;
      setLoadingCombined(true);
      setErrorCombined(null);
      try {
        const res = await executeQuery({
          cubeName: CUBE_NAME,
          measures,
          rows: [
            {
              dimensionName: DIM_COULEUR,
              hierarchyName: HIER_COULEUR,
              levelName: LEVEL_COULEUR,
            },
            {
              dimensionName: DIM_TAILLE,
              hierarchyName: HIER_TAILLE,
              levelName: LEVEL_TAILLE,
            },
          ],
          columns:
            magasinFilter && magasinFilter.size > 0
              ? [
                  {
                    ...CUBE_DIMENSIONS.depot,
                  },
                ]
              : [],
          dateFrom: from || undefined,
          dateTo: to || undefined,
        });

        const rawCols: string[] = res.data.columns ?? [];
        const rawRows: any[][] = res.data.rows ?? [];

        if (magasinFilter && magasinFilter.size > 0) {
          // Filtrer les colonnes correspondant aux dépôts sélectionnés
          const filterLower = new Set(
            [...magasinFilter].map((m) => m.trim().toLowerCase()),
          );
          const keepIdx: number[] = rawCols.reduce(
            (acc: number[], col: string, i: number) => {
              if (i === 0 || i === 1) return acc;
              const suffix = col.split(" - ").pop()?.trim().toLowerCase() ?? "";
              if (filterLower.has(suffix)) acc.push(i);
              return acc;
            },
            [],
          );
          const filteredCols = [
            rawCols[0],
            rawCols[1],
            ...keepIdx.map((i) => rawCols[i]),
          ];
          const filteredRows = rawRows.map((row) => [
            row[0],
            row[1],
            ...keepIdx.map((i) => row[i]),
          ]);
          setColumnsCombined(filteredCols);
          setAllRowsCombined(
            filteredRows.filter(
              (row: any[]) =>
                String(row[0] ?? "")
                  .trim()
                  .toLowerCase() !== "all" &&
                String(row[1] ?? "")
                  .trim()
                  .toLowerCase() !== "all",
            ),
          );
        } else {
          setColumnsCombined(rawCols);
          setAllRowsCombined(
            rawRows.filter(
              (row: any[]) =>
                String(row[0] ?? "")
                  .trim()
                  .toLowerCase() !== "all" &&
                String(row[1] ?? "")
                  .trim()
                  .toLowerCase() !== "all",
            ),
          );
        }
        setHasRun(true);
      } catch (e: any) {
        setErrorCombined(e.message || "Erreur");
      } finally {
        setLoadingCombined(false);
      }
    },
    [],
  );

  // Run on mount
  useEffect(() => {
    runCombined(selMeasures, "", "");
  }, []); // eslint-disable-line

  const handleRun = () => {
    runCombined(selMeasures, dateFrom, dateTo, selectedMagasins);
  };

  // ── Compute tab data for both ─────────────────────────────────────────────
  const tabDataCombined = useMemo(
    () =>
      makeTabDataCombined(
        columnsCombined,
        allRowsCombined,
        searchCombined,
        topN,
        cubeMeasures,
      ),
    [columnsCombined, allRowsCombined, searchCombined, topN, cubeMeasures],
  );

  const anyLoading = loadingCombined;

  // ── Listes uniques pour les dropdowns Couleur & Taille ────────────────────
  const couleurList = useMemo(() => {
    const seen = new Set<string>();
    allRowsCombined.forEach((row) => {
      const v = String(row[0] ?? "").trim();
      if (v && v.toLowerCase() !== "all") seen.add(v);
    });
    return Array.from(seen).sort();
  }, [allRowsCombined]);

  const tailleList = useMemo(() => {
    const seen = new Set<string>();
    allRowsCombined.forEach((row) => {
      const v = String(row[1] ?? "").trim();
      if (v && v.toLowerCase() !== "all") seen.add(v);
    });
    return Array.from(seen).sort();
  }, [allRowsCombined]);

  const demarquePhases = soldeSelected
    ? buildDemarquePhases(soldeSelected)
    : [];

  const measureGroupDefs = [
    {
      label: "Vente",
      color: NW.rose,
      accent: NW.roseLight,
      icon: "🛒",
      items: venteMeasures,
    },
    {
      label: "Achat",
      color: NW.violet,
      accent: NW.violetLight,
      icon: "📦",
      items: achatMeasures,
    },
    ...(otherMeasures.length > 0
      ? [
          {
            label: "Autres",
            color: NW.teal,
            accent: NW.tealLight,
            icon: "◈",
            items: otherMeasures,
          },
        ]
      : []),
  ];

  const PINNED_VENTE_KEYS = ["qte", "remise", "phtarem", "tva", "pttc"];
  const PINNED_ACHAT_KEYS = ["qte", "remise", "phtarem", "tva", "pttc"];

  const pinnedGroupDefs = measureGroupDefs.map((grp, gi) => {
    const keys =
      gi === 0 ? PINNED_VENTE_KEYS : gi === 1 ? PINNED_ACHAT_KEYS : [];
    if (keys.length === 0) return { ...grp, items: grp.items };
    const pinned: typeof cubeMeasures = [];
    keys.forEach((k) => {
      const found = grp.items.find(
        (m) =>
          m.name
            .toLowerCase()
            .replace(/[\s\-]/g, "")
            .includes(k) ||
          m.caption
            .toLowerCase()
            .replace(/[\s\-]/g, "")
            .includes(k),
      );
      if (found && !pinned.includes(found)) pinned.push(found);
    });
    return {
      ...grp,
      items: pinned.length > 0 ? pinned : grp.items.slice(0, 5),
    };
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: FONTS.body,
        padding: "18px 20px",
        maxWidth: 1700,
        background: NW.gray50,
        minHeight: "100vh",
      }}
    >
      <style>{`
        @keyframes nwFadeSlideIn { from { opacity: 0; transform: translateY(12px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes nwPulseGreen { 0%, 100% { box-shadow: 0 0 0 0 rgba(0,160,140,0.4); } 50% { box-shadow: 0 0 0 6px rgba(0,160,140,0); } }
        @keyframes nwPulseRed { 0%, 100% { box-shadow: 0 0 0 0 rgba(214,69,69,0.4); } 50% { box-shadow: 0 0 0 6px rgba(214,69,69,0); } }
        @keyframes nwDropDown { from { opacity: 0; transform: translateY(-6px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .nw-chip-btn:hover { filter: brightness(1.12); transform: translateY(-1px); }
        .nw-row-hover:hover td { background: ${NW.violetSoft} !important; }
        .nw-dropdown-item:hover { background: rgba(29,91,168,0.07) !important; }
        .nw-view-btn { transition: all 0.15s; }
        .nw-view-btn:hover { filter: brightness(1.08); }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${NW.violetMed}; border-radius: 99px; }
        .spinner { border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; display: inline-block; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ══ PAGE HEADER ════════════════════════════════════════════════════ */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 55%, #1D5BA8 100%)`,
          borderRadius: 16,
          padding: "18px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 16,
          boxShadow: "0 6px 32px rgba(29,91,168,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${NW.roseMed}80, ${NW.roseMed}CC, ${NW.roseMed}80, transparent)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          🎨
        </div>
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 20,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Analyse Couleur &amp; Taille
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Vue simultanée — Ventes · Marges · Coûts TTC
          </div>
        </div>
        {/* Combined KPI pill */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {selectedMagasins.size > 0 && (
            <div
              style={{
                textAlign: "center",
                background: "rgba(29,91,168,0.25)",
                border: "1px solid rgba(176,203,235,0.5)",
                borderRadius: 12,
                padding: "8px 18px",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: NW.indigoMed,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                  maxWidth: 160,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                🏪{" "}
                {selectedMagasins.size === 1
                  ? [...selectedMagasins][0]
                  : `${selectedMagasins.size} magasins`}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Magasin
              </div>
            </div>
          )}
          {allRowsCombined.length > 0 && (
            <div
              style={{
                textAlign: "center",
                background: "rgba(127,200,189,0.18)",
                border: "1px solid rgba(127,200,189,0.35)",
                borderRadius: 12,
                padding: "8px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#B0CBEB",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {allRowsCombined.length}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                🎨📏 Combinaisons
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ CONTROLS CARD ═══════════════════════════════════════════════════ */}
      <div style={card({ padding: "16px 20px", marginBottom: 14 })}>
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          {/* Measure groups */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pinnedGroupDefs.map((grp, gi) => {
              const isVente = gi === 0;
              const isAchat = gi === 1;
              const fullGrp = measureGroupDefs[gi];
              const pinnedNames = new Set(grp.items.map((p) => p.name));
              const extraSelected = (fullGrp?.items ?? []).filter(
                (m) => !pinnedNames.has(m.name) && selMeasures.includes(m.name),
              );
              const extraCount = extraSelected.length;

              const openPicker = () => {
                setPickerSearch("");
                setPopupInitialMeasures([...selMeasures]);
                if (isVente) setShowMeasurePickerVente(true);
                else if (isAchat) setShowMeasurePickerAchat(true);
              };

              return grp.items.length > 0 ? (
                <div key={grp.label}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: grp.color,
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{grp.icon}</span>
                    {grp.label}
                    {extraCount > 0 && (
                      <span
                        style={{
                          background: grp.color,
                          color: "#FFFFFF",
                          borderRadius: 99,
                          fontSize: 9,
                          fontWeight: 800,
                          padding: "1px 6px",
                          lineHeight: "14px",
                        }}
                      >
                        {extraCount}✓
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 5,
                      alignItems: "center",
                    }}
                  >
                    {grp.items.map((m) => {
                      const checked = selMeasures.includes(m.name);
                      return (
                        <label
                          key={m.name}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            cursor: "pointer",
                            background: checked ? grp.accent : NW.white,
                            border: `1.5px solid ${checked ? grp.color : NW.gray200}`,
                            borderRadius: 9,
                            padding: "4px 11px",
                            color: checked ? grp.color : NW.gray700,
                            fontWeight: checked ? 700 : 400,
                            transition: "all 0.15s",
                            boxShadow: checked
                              ? `0 0 0 2px ${grp.color}22`
                              : undefined,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMeasure(m.name)}
                            style={{ accentColor: grp.color, margin: 0 }}
                          />
                          {m.caption}
                        </label>
                      );
                    })}
                    {extraSelected.map((m) => (
                      <span
                        key={m.name}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 700,
                          background: grp.color,
                          color: "#FFFFFF",
                          borderRadius: 9,
                          padding: "4px 10px",
                          boxShadow: `0 2px 8px ${grp.color}44`,
                          transition: "all 0.15s",
                        }}
                      >
                        {m.caption}
                        <button
                          onClick={() => toggleMeasure(m.name)}
                          title="Retirer cette mesure"
                          style={{
                            background: "rgba(255,255,255,0.25)",
                            border: "none",
                            borderRadius: 5,
                            color: "#FFFFFF",
                            cursor: "pointer",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: "0 3px",
                            fontWeight: 700,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {(isVente || isAchat) && (
                      <button
                        onClick={openPicker}
                        title={`Choisir d'autres mesures ${grp.label}`}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: `1.5px dashed ${grp.color}88`,
                          background: NW.white,
                          color: grp.color,
                          fontSize: 17,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.15s",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                        onMouseEnter={(e) => {
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = grp.accent;
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.borderStyle = "solid";
                        }}
                        onMouseLeave={(e) => {
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = NW.white;
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.borderStyle = "dashed";
                        }}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              ) : null;
            })}
          </div>

          <div
            style={{
              width: 1,
              height: 50,
              background: NW.gray200,
              flexShrink: 0,
              alignSelf: "center",
            }}
          />

          {/* Date filter */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray800,
              }}
            >
              📅 Période
            </span>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                color: NW.gray500,
              }}
            >
              De :
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  border: `1.5px solid ${NW.gray200}`,
                  borderRadius: 8,
                  padding: "5px 10px",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: NW.gray800,
                  outline: "none",
                  background: NW.white,
                }}
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                color: NW.gray500,
              }}
            >
              À :
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  border: `1.5px solid ${NW.gray200}`,
                  borderRadius: 8,
                  padding: "5px 10px",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: NW.gray800,
                  outline: "none",
                  background: NW.white,
                }}
              />
            </label>
            {(["week", "month", "year"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setQuickPeriod(t)}
                style={{
                  fontSize: 11,
                  padding: "3px 11px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1px solid ${NW.violet}55`,
                  background: NW.violetLight,
                  color: NW.violet,
                  fontWeight: 700,
                  fontFamily: FONTS.body,
                }}
              >
                {t === "week" ? "7j" : t === "month" ? "1m" : "1an"}
              </button>
            ))}
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 8,
                  border: `1.5px solid ${NW.gray200}`,
                  background: NW.white,
                  color: NW.gray500,
                  cursor: "pointer",
                  fontFamily: FONTS.body,
                }}
              >
                ✕ Effacer
              </button>
            )}

            {/* Solde */}
            <div
              ref={soldeDropRef}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 700,
                  fontSize: 12,
                  color: NW.gray800,
                }}
              >
                🏷️ Solde
              </span>
              <button
                onClick={handleSoldeOpen}
                style={{
                  fontSize: 11,
                  padding: "4px 13px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1.5px solid ${soldeSelected ? "#00A08C" : NW.gray200}`,
                  background: soldeSelected ? "#D3EFEA" : NW.white,
                  color: soldeSelected ? "#006357" : NW.gray500,
                  fontWeight: soldeSelected ? 700 : 500,
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: FONTS.body,
                }}
              >
                {soldeSelected ? soldeSelected.code_periode : "Choisir période"}
                <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
              </button>
              {soldeSelected && (
                <button
                  onClick={handleSoldeClear}
                  style={{
                    fontSize: 11,
                    padding: "3px 9px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: "1px solid #A9DFD7",
                    background: "#EDF8F6",
                    color: "#006357",
                    fontWeight: 700,
                    fontFamily: FONTS.body,
                  }}
                >
                  ✕
                </button>
              )}
              {soldeSelected && soldeArticleCodes.size > 0 && (
                <span
                  style={{
                    background: "#D3EFEA",
                    color: "#006357",
                    border: "1px solid #A9DFD7",
                    borderRadius: 99,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  🏷️ {soldeArticleCodes.size} articles
                </span>
              )}
              {soldeOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    background: NW.white,
                    borderRadius: 12,
                    boxShadow: "0 8px 30px rgba(29,91,168,0.15)",
                    minWidth: 300,
                    maxHeight: 340,
                    overflowY: "auto",
                    zIndex: 9999,
                    border: "1.5px solid #A9DFD7",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 16px",
                      background: "linear-gradient(135deg, #006357, #00A08C)",
                      borderRadius: "10px 10px 0 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>🏷️</span>
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#FFFFFF",
                      }}
                    >
                      Périodes de Solde
                    </span>
                  </div>
                  {soldeLoading ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 12,
                      }}
                    >
                      Chargement…
                    </div>
                  ) : soldeError ? (
                    <div
                      style={{
                        padding: 16,
                        color: NW.danger,
                        fontSize: 12,
                        background: NW.dangerLight,
                        margin: 10,
                        borderRadius: 8,
                      }}
                    >
                      ⚠ {soldeError}
                    </div>
                  ) : soldePeriodes.length === 0 ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 12,
                        fontStyle: "italic",
                      }}
                    >
                      Aucune période disponible
                    </div>
                  ) : (
                    soldePeriodes.map((p) => {
                      const isActive =
                        soldeSelected?.code_periode === p.code_periode;
                      const fmtD = (d: string | null) =>
                        d ? new Date(d).toLocaleDateString("fr-FR") : "—";
                      return (
                        <div
                          key={p.code_periode}
                          onClick={() => handleSoldePeriodeSelect(p)}
                          style={{
                            padding: "10px 16px",
                            cursor: "pointer",
                            background: isActive ? "#D3EFEA" : NW.white,
                            borderBottom: `1px solid ${NW.gray100}`,
                            transition: "background 0.1s",
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                          onMouseEnter={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              isActive ? "#A9DFD7" : "#F8FAFC")
                          }
                          onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              isActive ? "#D3EFEA" : NW.white)
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {isActive && (
                              <span style={{ color: "#00A08C", fontSize: 12 }}>
                                ✓
                              </span>
                            )}
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 12,
                                color: isActive ? "#006357" : NW.gray800,
                              }}
                            >
                              {p.code_periode}
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              color: NW.gray500,
                              marginLeft: isActive ? 20 : 0,
                            }}
                          >
                            {fmtD(p.date_debut)} → {fmtD(p.date_fin)}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Démarque */}
            <div
              ref={demarqueDropRef}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => soldeSelected && setDemarqueOpen((v) => !v)}
                disabled={!soldeSelected}
                style={{
                  fontSize: 11,
                  padding: "4px 13px",
                  borderRadius: 99,
                  cursor: soldeSelected ? "pointer" : "not-allowed",
                  border: `1.5px solid ${selectedDemarquePhase ? "#1D5BA8" : demarqueOpen ? "#1D5BA8" : soldeSelected ? "#1D5BA855" : NW.gray200}`,
                  background: selectedDemarquePhase
                    ? "#1D5BA8"
                    : demarqueOpen
                      ? "#D7E5F5"
                      : soldeSelected
                        ? "#F1F5F9"
                        : NW.gray100,
                  color: selectedDemarquePhase
                    ? "#FFFFFF"
                    : soldeSelected
                      ? "#184B8C"
                      : NW.gray300,
                  fontWeight: soldeSelected ? 700 : 500,
                  opacity: soldeSelected ? 1 : 0.55,
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: FONTS.body,
                }}
              >
                📉{" "}
                {selectedDemarquePhase
                  ? selectedDemarquePhase.label
                  : "Démarque"}
                <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
              </button>
              {selectedDemarquePhase && (
                <button
                  onClick={handleDemarqueClear}
                  style={{
                    marginLeft: 4,
                    fontSize: 11,
                    padding: "3px 9px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: "1px solid #B0CBEB",
                    background: "#D7E5F5",
                    color: "#184B8C",
                    fontWeight: 700,
                    fontFamily: FONTS.body,
                  }}
                >
                  ✕
                </button>
              )}
              {demarqueOpen &&
                soldeSelected &&
                (() => {
                  const phases = buildDemarquePhases(soldeSelected);
                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        background: NW.white,
                        borderRadius: 12,
                        boxShadow: "0 8px 30px rgba(29,91,168,0.18)",
                        minWidth: 260,
                        zIndex: 9999,
                        border: "1.5px solid #B0CBEB",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 14px",
                          background:
                            "linear-gradient(135deg, #184B8C, #1D5BA8)",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>📉</span>
                        <span
                          style={{
                            fontFamily: FONTS.heading,
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#FFFFFF",
                          }}
                        >
                          Phases de démarque
                        </span>
                      </div>
                      {phases.length === 0 ? (
                        <div
                          style={{
                            padding: 16,
                            fontSize: 12,
                            color: NW.gray500,
                            textAlign: "center",
                            fontStyle: "italic",
                          }}
                        >
                          Aucune phase de démarque
                        </div>
                      ) : (
                        phases.map((phase) => {
                          const isActive =
                            selectedDemarquePhase?.label === phase.label;
                          return (
                            <div
                              key={phase.label}
                              onClick={() => handleDemarquePhaseSelect(phase)}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                background: isActive ? "#D7E5F5" : NW.white,
                                borderBottom: `1px solid ${NW.gray100}`,
                                transition: "background 0.1s",
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                              onMouseEnter={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#D7E5F5"
                                  : "#F1F5F9")
                              }
                              onMouseLeave={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#D7E5F5"
                                  : NW.white)
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {isActive && (
                                  <span
                                    style={{ color: "#1D5BA8", fontSize: 12 }}
                                  >
                                    ✓
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: isActive ? "#184B8C" : NW.gray800,
                                    fontFamily: FONTS.heading,
                                  }}
                                >
                                  {phase.icon} {phase.label}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: 10,
                                  color: NW.gray500,
                                  marginLeft: isActive ? 20 : 0,
                                }}
                              >
                                {phase.dateFrom} → {phase.dateTo}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })()}
            </div>

            {/* ── 🏪 Filtre Magasin — multi-sélection checkboxes ── */}
            <div
              ref={magasinDropRef}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {isRestricted ? (
                /* Pill statique non-cliquable pour vendeur / responsable_boutique */
                <span
                  style={{
                    fontSize: 11,
                    padding: "4px 13px",
                    borderRadius: 99,
                    border: `1.5px solid ${NW.indigo}`,
                    background: NW.indigo,
                    color: "#FFFFFF",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                    fontFamily: FONTS.body,
                    userSelect: "none",
                  }}
                >
                  🏪 {[...selectedMagasins][0] ?? userDepot}
                </span>
              ) : (
                <>
                  <button
                    onClick={handleMagasinOpen}
                    title="Filtrer par magasin (dépôt)"
                    style={{
                      fontSize: 11,
                      padding: "4px 13px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1.5px solid ${selectedMagasins.size > 0 ? NW.indigo : magasinOpen ? NW.indigo : NW.gray200}`,
                      background:
                        selectedMagasins.size > 0
                          ? NW.indigo
                          : magasinOpen
                            ? NW.indigoLight
                            : NW.white,
                      color:
                        selectedMagasins.size > 0
                          ? "#FFFFFF"
                          : magasinOpen
                            ? NW.indigoDark
                            : NW.gray500,
                      fontWeight:
                        selectedMagasins.size > 0 || magasinOpen ? 700 : 500,
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                      fontFamily: FONTS.body,
                      boxShadow:
                        selectedMagasins.size > 0
                          ? "0 2px 8px rgba(29,91,168,0.30)"
                          : magasinOpen
                            ? "0 0 0 2px " + NW.indigoMed
                            : "none",
                    }}
                  >
                    🏪{" "}
                    {selectedMagasins.size > 0
                      ? selectedMagasins.size === 1
                        ? [...selectedMagasins][0]
                        : `Magasin (${selectedMagasins.size})`
                      : magasinList.length > 0
                        ? `Magasin (${magasinList.length})`
                        : "Magasin"}
                    <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
                  </button>
                  {selectedMagasins.size > 0 && (
                    <button
                      onClick={handleMagasinClear}
                      style={{
                        marginLeft: 2,
                        fontSize: 11,
                        padding: "3px 9px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: "1px solid " + NW.indigoMed,
                        background: NW.indigoLight,
                        color: NW.indigoDark,
                        fontWeight: 700,
                        fontFamily: FONTS.body,
                      }}
                    >
                      ✕
                    </button>
                  )}
                  {magasinOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        background: NW.white,
                        borderRadius: 12,
                        boxShadow: "0 8px 30px rgba(29,91,168,0.18)",
                        minWidth: 240,
                        zIndex: 9999,
                        border: "1.5px solid " + NW.indigoMed,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        animation: "nwDropDown 0.15s ease",
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 16px",
                          background:
                            "linear-gradient(135deg, #133B6E, #1D5BA8)",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>🏪</span>
                        <div>
                          <div
                            style={{
                              fontFamily: FONTS.heading,
                              fontWeight: 700,
                              fontSize: 12,
                              color: "#FFFFFF",
                            }}
                          >
                            Filtrer par Magasin
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "rgba(255,255,255,0.6)",
                            }}
                          >
                            Sélectionnez un ou plusieurs dépôts
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "8px 12px",
                          borderBottom: "1px solid " + NW.gray100,
                          flexShrink: 0,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Rechercher…"
                          value={magasinSearch}
                          onChange={(e) => setMagasinSearch(e.target.value)}
                          autoFocus
                          style={{
                            width: "100%",
                            border: "1.5px solid " + NW.indigoMed,
                            borderRadius: 8,
                            padding: "5px 10px",
                            fontSize: 12,
                            fontFamily: FONTS.body,
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div style={{ overflowY: "auto", maxHeight: 220 }}>
                        {magasinLoading ? (
                          <div
                            style={{
                              padding: 20,
                              textAlign: "center",
                              color: NW.gray500,
                              fontSize: 12,
                            }}
                          >
                            ⏳ Chargement des magasins…
                          </div>
                        ) : magasinList.length === 0 ? (
                          <div
                            style={{
                              padding: 20,
                              textAlign: "center",
                              color: NW.gray500,
                              fontSize: 12,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span style={{ fontStyle: "italic" }}>
                              Aucun magasin trouvé
                            </span>
                            <button
                              onClick={loadMagasins}
                              style={{
                                fontSize: 11,
                                padding: "4px 14px",
                                borderRadius: 8,
                                border: "1.5px solid " + NW.indigoMed,
                                background: NW.indigoLight,
                                color: NW.indigoDark,
                                cursor: "pointer",
                                fontWeight: 700,
                                fontFamily: FONTS.body,
                              }}
                            >
                              🔄 Réessayer
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Sous-header "Tout sélectionner" */}
                            <div
                              style={{
                                padding: "6px 12px",
                                borderBottom: "1px solid " + NW.gray100,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: NW.gray50,
                                flexShrink: 0,
                              }}
                            >
                              <label
                                style={{
                                  fontSize: 11,
                                  color: NW.gray500,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  userSelect: "none",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    magasinList
                                      .filter(
                                        (m) =>
                                          magasinSearch === "" ||
                                          m
                                            .toLowerCase()
                                            .includes(
                                              magasinSearch.toLowerCase(),
                                            ),
                                      )
                                      .every((m) => selectedMagasins.has(m)) &&
                                    magasinList.filter(
                                      (m) =>
                                        magasinSearch === "" ||
                                        m
                                          .toLowerCase()
                                          .includes(
                                            magasinSearch.toLowerCase(),
                                          ),
                                    ).length > 0
                                  }
                                  onChange={(e) => {
                                    const visible = magasinList.filter(
                                      (m) =>
                                        magasinSearch === "" ||
                                        m
                                          .toLowerCase()
                                          .includes(
                                            magasinSearch.toLowerCase(),
                                          ),
                                    );
                                    setSelectedMagasins((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked)
                                        visible.forEach((m) => next.add(m));
                                      else
                                        visible.forEach((m) => next.delete(m));
                                      return next;
                                    });
                                  }}
                                  style={{
                                    accentColor: NW.indigo,
                                    cursor: "pointer",
                                  }}
                                />
                                Tout sélectionner
                              </label>
                              {selectedMagasins.size > 0 && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: NW.indigo,
                                    fontWeight: 700,
                                  }}
                                >
                                  {selectedMagasins.size} sélectionné
                                  {selectedMagasins.size > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>

                            {/* Liste checkboxes */}
                            {magasinList
                              .filter(
                                (m) =>
                                  magasinSearch === "" ||
                                  m
                                    .toLowerCase()
                                    .includes(magasinSearch.toLowerCase()),
                              )
                              .map((mag) => {
                                const isActive = selectedMagasins.has(mag);
                                return (
                                  <label
                                    key={mag}
                                    style={{
                                      padding: "9px 16px",
                                      cursor: "pointer",
                                      background: isActive
                                        ? NW.indigoLight
                                        : NW.white,
                                      borderBottom: "1px solid " + NW.gray100,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      fontSize: 12,
                                      fontWeight: isActive ? 700 : 400,
                                      color: isActive
                                        ? NW.indigoDark
                                        : NW.gray800,
                                      userSelect: "none",
                                    }}
                                    onMouseEnter={(e) =>
                                      ((
                                        e.currentTarget as HTMLElement
                                      ).style.background = isActive
                                        ? NW.indigoMed
                                        : NW.gray50)
                                    }
                                    onMouseLeave={(e) =>
                                      ((
                                        e.currentTarget as HTMLElement
                                      ).style.background = isActive
                                        ? NW.indigoLight
                                        : NW.white)
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isActive}
                                      onChange={() => handleMagasinToggle(mag)}
                                      style={{
                                        accentColor: NW.indigo,
                                        width: 14,
                                        height: 14,
                                        cursor: "pointer",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span style={{ fontFamily: FONTS.body }}>
                                      {mag}
                                    </span>
                                  </label>
                                );
                              })}
                          </>
                        )}
                      </div>

                      {/* Footer Appliquer */}
                      <div
                        style={{
                          padding: "8px 12px",
                          borderTop: "1px solid " + NW.gray100,
                          flexShrink: 0,
                        }}
                      >
                        <button
                          onClick={handleMagasinApply}
                          style={{
                            width: "100%",
                            padding: "7px 0",
                            borderRadius: 8,
                            border: "none",
                            background: `linear-gradient(135deg, ${NW.indigoDark}, ${NW.indigo})`,
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: 12,
                            fontFamily: FONTS.body,
                            cursor: "pointer",
                          }}
                        >
                          ✓ Appliquer ({selectedMagasins.size} sélectionné
                          {selectedMagasins.size > 1 ? "s" : ""})
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Run + Save + TopN + ViewMode */}
          <div
            style={{
              marginLeft: "auto",
              alignSelf: "flex-end",
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Top N */}
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span
                style={{ fontSize: 11, color: NW.gray500, fontWeight: 600 }}
              >
                Top
              </span>
              {[...TOP_OPTIONS, null].map((n) => (
                <button
                  key={String(n)}
                  onClick={() => setTopN(n)}
                  style={{
                    fontSize: 11,
                    padding: "4px 10px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: `1.5px solid ${topN === n ? NW.violet : NW.gray200}`,
                    background: topN === n ? NW.violetLight : NW.white,
                    color: topN === n ? NW.violet : NW.gray500,
                    fontWeight: topN === n ? 700 : 400,
                    fontFamily: FONTS.body,
                    transition: "all 0.12s",
                  }}
                >
                  {n === null ? "Tous" : n}
                </button>
              ))}
            </div>

            <div style={{ width: 1, height: 28, background: NW.gray200 }} />

            {/* View mode */}
            <div style={{ display: "flex", gap: 4 }}>
              {VIEW_MODES.map(({ mode, icon, label }) => (
                <button
                  key={mode}
                  className="nw-view-btn"
                  onClick={() => setViewMode(mode)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 12px",
                    border: `1.5px solid ${viewMode === mode ? NW.violet : NW.gray200}`,
                    borderRadius: 9,
                    background: viewMode === mode ? NW.violetLight : NW.white,
                    color: viewMode === mode ? NW.violet : NW.gray500,
                    fontWeight: viewMode === mode ? 700 : 400,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 13 }}>{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            <div style={{ width: 1, height: 28, background: NW.gray200 }} />

            <SavedQueriesDropdown
              refreshToken={savedRefreshToken}
              currentType={"couleur-taille"}
              onSelect={(q) => {
                setSelMeasures(q.measures);
                if (q.topN != null) setTopN(q.topN);
                const validModes: ViewMode[] = [
                  "tableau",
                  "dashboard",
                  "comparaison",
                  "evolution",
                ];
                if (q.section && validModes.includes(q.section as ViewMode))
                  setViewMode(q.section as ViewMode);
                setSoldeSelected(null);
                setSoldeArticleCodes(new Set());
                setDateFrom(q.dateFrom ?? "");
                setDateTo(q.dateTo ?? "");
                runCombined(
                  q.measures,
                  q.dateFrom ?? "",
                  q.dateTo ?? "",
                  selectedMagasins,
                );
              }}
            />

            <button
              onClick={() => hasRun && setShowSaveModal(true)}
              disabled={!hasRun}
              style={{
                padding: "9px 16px",
                background: hasRun
                  ? `linear-gradient(135deg, ${NW.violet}, ${NW.violetDark})`
                  : NW.gray200,
                color: hasRun ? "#FFFFFF" : NW.gray500,
                border: "none",
                borderRadius: 10,
                cursor: hasRun ? "pointer" : "not-allowed",
                fontWeight: 700,
                fontSize: 12,
                fontFamily: FONTS.body,
                boxShadow: hasRun ? "0 4px 14px rgba(29,91,168,0.28)" : "none",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              💾 Enregistrer
            </button>

            <button
              onClick={handleRun}
              disabled={anyLoading || selMeasures.length === 0}
              style={{
                padding: "9px 22px",
                background:
                  anyLoading || selMeasures.length === 0
                    ? NW.gray200
                    : NW.violet,
                color:
                  anyLoading || selMeasures.length === 0 ? NW.gray500 : "#FFFFFF",
                border: "none",
                borderRadius: 10,
                cursor:
                  anyLoading || selMeasures.length === 0
                    ? "default"
                    : "pointer",
                fontWeight: 700,
                fontSize: 13,
                fontFamily: FONTS.body,
                boxShadow:
                  anyLoading || selMeasures.length === 0
                    ? "none"
                    : "0 4px 16px rgba(29,91,168,0.28)",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {anyLoading ? (
                <>
                  <span
                    className="spinner"
                    style={{ width: 14, height: 14, flexShrink: 0 }}
                  />
                  Chargement…
                </>
              ) : (
                <>▶ Calculer</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ══ COMBINED PANEL ══════════════════════════════════════════════════ */}
      <CombinedPanel
        columns={columnsCombined}
        allRows={allRowsCombined}
        loading={loadingCombined}
        error={errorCombined}
        search={searchCombined}
        setSearch={setSearchCombined}
        topN={topN}
        viewMode={viewMode}
        selMeasures={selMeasures}
        tabData={tabDataCombined}
        couleurList={couleurList}
        tailleList={tailleList}
        selCouleur={selCouleur}
        setSelCouleur={setSelCouleur}
        selTaille={selTaille}
        setSelTaille={setSelTaille}
      />

      {/* ══ MEASURE PICKER — VENTE ════════════════════════════════════════ */}
      {showMeasurePickerVente &&
        (() => {
          const grp = measureGroupDefs.find((g) => g.label === "Vente")!;
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.52)",
                zIndex: 10000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(3px)",
                animation: "nwFadeSlideIn 0.18s ease",
              }}
              onClick={() => setShowMeasurePickerVente(false)}
            >
              <div
                style={{
                  background: NW.white,
                  borderRadius: 18,
                  boxShadow: "0 24px 80px rgba(127,200,189,0.22)",
                  width: "min(640px, 95vw)",
                  maxHeight: "78vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${NW.roseDark}, ${NW.rose})`,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>🛒</span>
                  <div>
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 800,
                        fontSize: 15,
                        color: "#FFFFFF",
                      }}
                    >
                      Mesures Vente
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.65)",
                        marginTop: 2,
                      }}
                    >
                      {
                        selMeasures.filter((n) =>
                          grp.items.some((m) => m.name === n),
                        ).length
                      }{" "}
                      sélectionnée(s)
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMeasurePickerVente(false)}
                    style={{
                      marginLeft: "auto",
                      background: "rgba(255,255,255,0.18)",
                      border: "none",
                      borderRadius: 8,
                      color: "#FFFFFF",
                      fontSize: 16,
                      width: 30,
                      height: 30,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    padding: "10px 20px",
                    borderBottom: `1px solid ${NW.gray100}`,
                  }}
                >
                  <input
                    autoFocus
                    type="text"
                    placeholder="🔍 Rechercher une mesure…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    style={{
                      width: "100%",
                      border: `1.5px solid ${NW.roseMed}`,
                      borderRadius: 9,
                      padding: "7px 12px",
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: NW.gray800,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    flex: 1,
                    padding: "14px 20px 20px",
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(() => {
                      const pinnedNames = new Set(
                        (
                          pinnedGroupDefs.find((g) => g.label === "Vente")
                            ?.items ?? []
                        ).map((p) => p.name),
                      );
                      return grp.items
                        .filter((m) => !pinnedNames.has(m.name))
                        .filter(
                          (m) =>
                            !pickerSearch ||
                            m.caption
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()) ||
                            m.name
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()),
                        )
                        .map((m) => {
                          const checked = selMeasures.includes(m.name);
                          return (
                            <label
                              key={m.name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                cursor: "pointer",
                                background: checked ? NW.roseLight : NW.gray50,
                                border: `1.5px solid ${checked ? NW.rose : NW.gray200}`,
                                borderRadius: 9,
                                padding: "5px 12px",
                                color: checked ? NW.rose : NW.gray700,
                                fontWeight: checked ? 700 : 400,
                                transition: "all 0.12s",
                                boxShadow: checked
                                  ? `0 0 0 2px ${NW.rose}22`
                                  : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMeasure(m.name)}
                                style={{ accentColor: NW.rose, margin: 0 }}
                              />
                              {m.caption}
                            </label>
                          );
                        });
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: `1px solid ${NW.gray100}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    background: NW.gray50,
                  }}
                >
                  <button
                    onClick={() => {
                      setSelMeasures(popupInitialMeasures);
                      setShowMeasurePickerVente(false);
                    }}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: `1.5px solid ${NW.danger}`,
                      background: NW.white,
                      color: NW.danger,
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                    }}
                  >
                    ✕ Annuler
                  </button>
                  <button
                    onClick={() => setShowMeasurePickerVente(false)}
                    style={{
                      padding: "9px 24px",
                      border: "none",
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${NW.roseDark}, ${NW.rose})`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      boxShadow: `0 4px 16px rgba(127,200,189,0.30)`,
                    }}
                  >
                    ✓ Valider
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ══ MEASURE PICKER — ACHAT ════════════════════════════════════════ */}
      {showMeasurePickerAchat &&
        (() => {
          const grp = measureGroupDefs.find((g) => g.label === "Achat")!;
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.52)",
                zIndex: 10000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(3px)",
                animation: "nwFadeSlideIn 0.18s ease",
              }}
              onClick={() => setShowMeasurePickerAchat(false)}
            >
              <div
                style={{
                  background: NW.white,
                  borderRadius: 18,
                  boxShadow: "0 24px 80px rgba(29,91,168,0.22)",
                  width: "min(640px, 95vw)",
                  maxHeight: "78vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>📦</span>
                  <div>
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 800,
                        fontSize: 15,
                        color: "#FFFFFF",
                      }}
                    >
                      Mesures Achat
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.65)",
                        marginTop: 2,
                      }}
                    >
                      {
                        selMeasures.filter((n) =>
                          grp.items.some((m) => m.name === n),
                        ).length
                      }{" "}
                      sélectionnée(s)
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMeasurePickerAchat(false)}
                    style={{
                      marginLeft: "auto",
                      background: "rgba(255,255,255,0.18)",
                      border: "none",
                      borderRadius: 8,
                      color: "#FFFFFF",
                      fontSize: 16,
                      width: 30,
                      height: 30,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    padding: "10px 20px",
                    borderBottom: `1px solid ${NW.gray100}`,
                  }}
                >
                  <input
                    autoFocus
                    type="text"
                    placeholder="🔍 Rechercher une mesure…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    style={{
                      width: "100%",
                      border: `1.5px solid ${NW.violetMed}`,
                      borderRadius: 9,
                      padding: "7px 12px",
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: NW.gray800,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    flex: 1,
                    padding: "14px 20px 20px",
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(() => {
                      const pinnedNames = new Set(
                        (
                          pinnedGroupDefs.find((g) => g.label === "Achat")
                            ?.items ?? []
                        ).map((p) => p.name),
                      );
                      return grp.items
                        .filter((m) => !pinnedNames.has(m.name))
                        .filter(
                          (m) =>
                            !pickerSearch ||
                            m.caption
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()) ||
                            m.name
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()),
                        )
                        .map((m) => {
                          const checked = selMeasures.includes(m.name);
                          return (
                            <label
                              key={m.name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                cursor: "pointer",
                                background: checked
                                  ? NW.violetLight
                                  : NW.gray50,
                                border: `1.5px solid ${checked ? NW.violet : NW.gray200}`,
                                borderRadius: 9,
                                padding: "5px 12px",
                                color: checked ? NW.violet : NW.gray700,
                                fontWeight: checked ? 700 : 400,
                                transition: "all 0.12s",
                                boxShadow: checked
                                  ? `0 0 0 2px ${NW.violet}22`
                                  : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMeasure(m.name)}
                                style={{ accentColor: NW.violet, margin: 0 }}
                              />
                              {m.caption}
                            </label>
                          );
                        });
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: `1px solid ${NW.gray100}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    background: NW.gray50,
                  }}
                >
                  <button
                    onClick={() => {
                      setSelMeasures(popupInitialMeasures);
                      setShowMeasurePickerAchat(false);
                    }}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: `1.5px solid ${NW.danger}`,
                      background: NW.white,
                      color: NW.danger,
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                    }}
                  >
                    ✕ Annuler
                  </button>
                  <button
                    onClick={() => setShowMeasurePickerAchat(false)}
                    style={{
                      padding: "9px 24px",
                      border: "none",
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      boxShadow: `0 4px 16px rgba(29,91,168,0.30)`,
                    }}
                  >
                    ✓ Valider
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ══ SAVE MODAL ════════════════════════════════════════════════════ */}
      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => setSavedRefreshToken((t) => t + 1)}
          type={"couleur-taille"}
          section={viewMode}
          overrideMeasures={selMeasures}
          overrideDateFrom={
            soldeSelected != null || selectedDemarquePhase != null || dateFrom
              ? dateFrom
              : null
          }
          overrideDateTo={
            soldeSelected != null || selectedDemarquePhase != null || dateTo
              ? dateTo
              : null
          }
          overrideTopN={topN}
          overrideSoldeCodePeriode={soldeSelected?.code_periode ?? null}
          overrideDemarquePhase={selectedDemarquePhase?.label ?? null}
        />
      )}
    </div>
  );
};

export default CouleurTailleView;
